// ── the private view · the pure half ─────────────────────────────────────────
//
// Everything innernet holds about one person, grouped so they can read it:
// every disclosure class, by dimension, private-leaning dimensions first, with
// a plain ledger of who gets to see each class.
//
// PURE — no database, no model. The route (app/api/you/private) reads the rows
// with the person's own session and hands them here. Nothing in this module or
// its output is ever sent to a model, to jev, or into an attribute.

import type { Disclosure, FactAuthor, FactSource, FactVersion, NoticeKind, PrivateFact, PrivatePattern, PrivatePayload, QuoteKind } from './types';
import type { FactVoice } from '../memory/voices';
import type { RecordRef } from './record';
import { aboutLead, aboutTag } from '../self/about';
import { machineOfSource, machineSource } from '../machines';

/** the dimension a group of facts about one person is given: 'about:<tag>' (never a real dimension) */
export const ABOUT_GROUP = 'about:';

/** who a group of facts about someone else is about, as a heading says it */
export const aboutLabel = (name: string | null | undefined): string => (name ? `about ${name}` : 'about someone in your life');

/**
 * Whose each fact about someone else is, for the page: the short tag every fact
 * about one person shares, and the name most of their facts lead with
 * (lib/self/about aboutLead). From words the route already opened; the name is
 * never stored. Pure.
 */
export function aboutOf(
  facts: ReadonlyArray<Pick<PrivateFact, 'id' | 'statement'>>,
  keyById: ReadonlyMap<string, string>,
): Map<string, { group: string; name: string | null }> {
  const votes = new Map<string, Map<string, number>>();
  for (const f of facts) {
    const tag = aboutTag(keyById.get(f.id));
    if (!tag) continue;
    const lead = aboutLead(f.statement);
    const m = votes.get(tag) ?? new Map<string, number>();
    if (lead) m.set(lead, (m.get(lead) ?? 0) + 1);
    votes.set(tag, m);
  }
  const nameOf = new Map<string, string | null>();
  for (const [tag, m] of votes) nameOf.set(tag, [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0] ?? null);
  const out = new Map<string, { group: string; name: string | null }>();
  for (const f of facts) {
    const tag = aboutTag(keyById.get(f.id));
    if (tag) out.set(f.id, { group: tag, name: nameOf.get(tag) ?? null });
  }
  return out;
}

/** Deepest first — the order the ledger and every fact list read in. */
export const DISCLOSURE_ORDER: readonly Disclosure[] = ['private', 'self', 'trusted', 'work', 'public'];

/** What each class means, in the house voice. `reach` is who can see it. */
export const LEDGER_COPY: Record<Disclosure, { meaning: string; reach: string }> = {
  private: {
    meaning: 'no room or share link ever sees it. an ai tool reads it only when you allow it, and each time is written down.',
    reach: 'you, and apps you allow',
  },
  self: {
    meaning: 'yours to read here. an ai tool reads it only when you allow it, and each time is written down.',
    reach: 'you, and apps you allow',
  },
  trusted: {
    meaning: 'read by the tools you connected yourself.',
    reach: 'your connected tools',
  },
  work: {
    meaning: 'read inside projects you marked as work.',
    reach: 'your work projects',
  },
  public: {
    meaning: 'you chose to share it.',
    reach: 'anyone you share with',
  },
};

/** Dimensions that lean private by nature (packages/core dimensions.ts, plus
 *  two invented names that carry the same weight). Shown first. */
export const PRIVATE_BIASED: readonly string[] = [
  'relationships',
  'kin',
  'somatic',
  'hauntings',
  'aversions',
  'identity',
  'episodic',
];

/** Anything that isn't one of the five classes is treated as the deepest one. */
export function normDisclosure(v: unknown): Disclosure {
  const s = String(v ?? '').toLowerCase();
  return (DISCLOSURE_ORDER as readonly string[]).includes(s) ? (s as Disclosure) : 'private';
}

/** A dimension's name as people read it: lowercase, words not underscores.
 *  (The registry's labels are exactly this, title-cased — the house is lowercase.) */
export function dimensionLabel(name: string): string {
  const s = String(name ?? '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return s || 'unsorted';
}

// ── the ledger ───────────────────────────────────────────────────────────────

export function buildLedger(facts: Array<{ disclosure: Disclosure }>): PrivatePayload['ledger'] {
  const counts = new Map<Disclosure, number>(DISCLOSURE_ORDER.map((d) => [d, 0]));
  for (const f of facts) counts.set(f.disclosure, (counts.get(f.disclosure) ?? 0) + 1);
  return DISCLOSURE_ORDER.map((disclosure) => ({
    disclosure,
    count: counts.get(disclosure) ?? 0,
    meaning: LEDGER_COPY[disclosure].meaning,
    reach: LEDGER_COPY[disclosure].reach,
  }));
}

// ── facts by dimension ───────────────────────────────────────────────────────

const rankOf = (d: Disclosure) => DISCLOSURE_ORDER.indexOf(d);

/**
 * Group every fact by dimension. Groups: private-leaning dimensions first (in
 * PRIVATE_BIASED order), then those holding the most private facts, then the
 * largest. Inside a group: deepest class first, newest first.
 *
 * A fact about someone else in the person's life (161) is not filed among the
 * person's own: each person gets a group of their own ('about:<tag>', "about
 * ravi"), after every group of the person's, the most facts first.
 */
export function groupFacts(facts: PrivateFact[]): PrivatePayload['groups'] {
  const own = groupOwn(facts.filter((f) => !f.about));
  const people = new Map<string, { name: string | null; list: PrivateFact[] }>();
  for (const f of facts) {
    if (!f.about) continue;
    const p = people.get(f.about.group) ?? { name: f.about.name, list: [] };
    p.name ??= f.about.name;
    p.list.push(f);
    people.set(f.about.group, p);
  }
  const theirs = [...people.entries()]
    .sort(([a, pa], [b, pb]) => pb.list.length - pa.list.length || a.localeCompare(b))
    .map(([group, p]) => ({
      dimension: `${ABOUT_GROUP}${group}`,
      label: aboutLabel(p.name),
      facts: [...p.list].sort((x, y) => rankOf(x.disclosure) - rankOf(y.disclosure) || y.createdAt.localeCompare(x.createdAt)),
    }));
  return [...own, ...theirs];
}

function groupOwn(facts: PrivateFact[]): PrivatePayload['groups'] {
  const by = new Map<string, PrivateFact[]>();
  for (const f of facts) {
    const key = String(f.dimension || 'unsorted');
    const list = by.get(key);
    if (list) list.push(f);
    else by.set(key, [f]);
  }
  const biased = (d: string) => {
    const i = PRIVATE_BIASED.indexOf(d);
    return i < 0 ? PRIVATE_BIASED.length : i;
  };
  const privateCount = (list: PrivateFact[]) => list.filter((f) => f.disclosure === 'private').length;

  return [...by.entries()]
    .sort(
      ([a, la], [b, lb]) =>
        biased(a) - biased(b) ||
        privateCount(lb) - privateCount(la) ||
        lb.length - la.length ||
        a.localeCompare(b),
    )
    .map(([dimension, list]) => ({
      dimension,
      label: dimensionLabel(dimension),
      facts: [...list].sort(
        (x, y) => rankOf(x.disclosure) - rankOf(y.disclosure) || y.createdAt.localeCompare(x.createdAt),
      ),
    }));
}

// ── what you used to hold true ───────────────────────────────────────────────

export interface RetiredFactRow {
  id: string;
  dimension: string | null;
  statement: string | null;
  valid_to: string | null;
}
export interface SuccessorFactRow {
  id: string;
  statement: string | null;
  supersedes: string | null;
  created_at?: string | null;
  written_by?: string | null;
}

/**
 * Superseded facts beside what replaced them, newest change first. A closed
 * version with no successor simply stopped holding (`replaced: false`); one
 * whose successor would not open reads as sealed (`replaced`, `now` null).
 */
export function buildHistory(
  retired: RetiredFactRow[],
  successors: SuccessorFactRow[],
  limit = 60,
): PrivatePayload['history'] {
  // when a fact was superseded more than once, the newest successor is "now"
  const next = new Map<string, SuccessorFactRow>();
  for (const s of successors) {
    if (!s.supersedes) continue;
    const cur = next.get(s.supersedes);
    if (!cur || String(s.created_at ?? '') > String(cur.created_at ?? '')) next.set(s.supersedes, s);
  }
  return retired
    .filter((r) => r.valid_to)
    .sort((a, b) => String(b.valid_to).localeCompare(String(a.valid_to)))
    .slice(0, limit)
    .map((r) => {
      const s = next.get(String(r.id));
      return {
        id: String(r.id),
        dimension: String(r.dimension ?? 'unsorted'),
        was: r.statement ?? null,
        now: s?.statement ?? null,
        changedAt: String(r.valid_to),
        replaced: !!s,
        nowId: s ? String(s.id) : null,
        by: s ? normAuthor(s.written_by) : null,
      };
    });
}

// ── versions: phases, not overwrites ─────────────────────────────────────────

/** A version as the route reads it (opened). */
export interface VersionRow {
  id: string;
  statement: string | null;
  disclosure_default?: string | null;
  written_by?: string | null;
  valid_from?: string | null;
  created_at?: string | null;
  valid_to?: string | null;
  supersedes?: string | null;
}

/** How far back one fact's history is walked (and listed). */
export const VERSION_CAP = 12;

export function normAuthor(v: unknown): FactAuthor {
  return v === 'you' ? 'you' : 'netti';
}

/**
 * Walk each current fact's `supersedes` chain back through `past` (the closed
 * versions the route read, by id) and hand back its versions, newest first,
 * and when it was first found (the oldest version's valid_from). A link that
 * points at a row not in `past` (forgotten, or past the cap) ends the chain
 * there. Pure: the route does the reading, round by round.
 */
export function walkVersions(
  current: Array<{ id: string; supersedes?: string | null; valid_from?: string | null; created_at?: string | null }>,
  past: ReadonlyMap<string, VersionRow>,
  cap = VERSION_CAP,
): Map<string, { versions: FactVersion[]; foundAt: string }> {
  const out = new Map<string, { versions: FactVersion[]; foundAt: string }>();
  for (const f of current) {
    const versions: FactVersion[] = [];
    const seen = new Set<string>([String(f.id)]);
    let foundAt = String(f.valid_from ?? f.created_at ?? '');
    let link = f.supersedes ? String(f.supersedes) : null;
    while (link && !seen.has(link) && versions.length < cap) {
      const row = past.get(link);
      if (!row) break;
      seen.add(link);
      const from = String(row.valid_from ?? row.created_at ?? '');
      versions.push({
        id: String(row.id),
        statement: row.statement == null ? null : String(row.statement) || null,
        disclosure: normDisclosure(row.disclosure_default),
        writtenBy: normAuthor(row.written_by),
        from,
        to: row.valid_to ? String(row.valid_to) : null,
      });
      if (from) foundAt = from;
      link = row.supersedes ? String(row.supersedes) : null;
    }
    out.set(String(f.id), { versions, foundAt });
  }
  return out;
}

/** Every id a fact's record has held (this version and every earlier one). */
export function chainOf(fact: Pick<PrivateFact, 'id' | 'versions'>): string[] {
  return [fact.id, ...(fact.versions ?? []).map((v) => v.id)];
}

// ── editing a fact ───────────────────────────────────────────────────────────

/** The longest a statement may be, in characters. */
export const STATEMENT_MAX = 600;

/** What a new version may say. The same check on the page and in lib/self/revise. */
export function checkStatement(
  next: string,
  before: { statement: string | null; disclosure: Disclosure },
  disclosure?: Disclosure | null,
): { ok: true; statement: string } | { ok: false; message: string } {
  const statement = String(next ?? '').replace(/\s+/g, ' ').trim();
  if (!statement) return { ok: false, message: 'please write what is true now.' };
  if (statement.length > STATEMENT_MAX) return { ok: false, message: `please keep it under ${STATEMENT_MAX} characters.` };
  const sameWords = statement === String(before.statement ?? '').replace(/\s+/g, ' ').trim();
  const sameClass = !disclosure || disclosure === before.disclosure;
  if (sameWords && sameClass) return { ok: false, message: 'nothing has changed yet.' };
  return { ok: true, statement };
}

/**
 * The one line above an edit's save: what the press will cause. When the class
 * widens, it names who will newly be able to read it.
 */
export function editBlastRadius(from: Disclosure, to: Disclosure): string {
  const lock = 'the old one stays, locked, in its history.';
  if (to === from) return `your words become the new version. ${lock}`;
  const wider = DISCLOSURE_ORDER.indexOf(to) > DISCLOSURE_ORDER.indexOf(from);
  if (to === 'private') return `your words become the new version, and it leaves innernet only for apps you allow. ${lock}`;
  if (to === 'self') return `your words become the new version, and a tool reads it only when you allow it. ${lock}`;
  const reach = LEDGER_COPY[to].reach;
  return wider
    ? `your words become the new version, and ${reach} can read it. ${lock}`
    : `your words become the new version, read only by ${reach}. ${lock}`;
}

// ── provenance, in words ─────────────────────────────────────────────────────

// fixed lowercase month names: Intl's en-GB short month reads 'sept', never the house 'sep'.
const MONTHS_SHORT = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTHS_LONG = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

/** "21 sep" (this year) or "21 sep 2025". Local time; `nowMs` only decides the year. */
export function shortDay(iso: string | null | undefined, nowMs: number): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
  return d.getFullYear() === new Date(nowMs).getFullYear() ? day : `${day} ${d.getFullYear()}`;
}

const SAID_IN = new Set(['chatgpt', 'claude', 'cursor', 'cline', 'antigravity', 'codex', 'gemini', 'discord']);

/**
 * Where a fact came from and when it was found, as the short parts of one line:
 * "found 21 sep" · "said in Claude on 20 sep" · "while working on innernet" ·
 * "you rewrote it 23 sep". Versions are counted by the page (they open).
 *
 * "said" is the person's own words, so it is used only when the fact's voice is
 * `said` (lib/memory/voices). A fact an AI's note reports, or netti read, was
 * "noted": "noted in Claude on 20 sep", "noted by netti", "noted on 20 sep".
 * A voice no writer stamped reads as not said.
 */
/**
 * When a fact is from, as its words say (158): "in 2019" · "in march 2024" ·
 * "on 14 mar 2024". The time it speaks of, said before when it was found.
 * Null when the fact carries no such time.
 */
export function fromWords(from: string | null | undefined): string | null {
  const m = String(from ?? '').match(/^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/);
  if (!m) return null;
  const month = m[2] ? MONTHS_LONG[Number(m[2]) - 1] : null;
  if (m[2] && !month) return null;
  if (m[3]) return `on ${Number(m[3])} ${MONTHS_SHORT[Number(m[2]) - 1]} ${m[1]}`;
  return month ? `in ${month} ${m[1]}` : `in ${m[1]}`;
}

/** The other kinds a fact is (158), as one quiet part: "also habits and behavior". Null when it is one kind. */
export function alsoWords(also: readonly string[] | null | undefined): string | null {
  const labels = (also ?? []).map(dimensionLabel).filter((l) => l && l !== 'unsorted');
  if (!labels.length) return null;
  return `also ${labels.length === 1 ? labels[0] : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`}`;
}

export function provenanceParts(f: PrivateFact, nowMs: number): string[] {
  const parts: string[] = [];
  // whose it is comes first: a fact about someone else in their life is never read as the person's (161)
  if (f.about) parts.push(aboutLabel(f.about.name));
  // what the fact is about comes before where it was found: when it is from, and the other kinds it is
  const from = fromWords(f.from);
  if (from) parts.push(from);
  const also = alsoWords(f.also);
  if (also) parts.push(also);
  const found = shortDay(f.foundAt ?? f.createdAt, nowMs);
  // with earlier versions, "found" is the first of them; "said" is this one's
  if (found) parts.push((f.versions?.length ?? 0) > 0 ? `first found ${found}` : `found ${found}`);

  // the source as words, whether it arrives raw ("chatgpt-backfill-…") or already named
  const src = f.source ? sourceLabel(String(f.source)) : null;
  const imported = /^import/i.test(String(f.phase ?? '')) || /import/i.test(String(src ?? ''));
  const said = shortDay(f.saidAt ?? null, nowMs);
  // only a fact that rests on the person's own words was "said"; the rest was noted
  const theirs = f.voice === 'said';
  if (imported) {
    parts.push(src && /import/i.test(src) ? `from ${src}` : 'from an import');
  } else if (src) {
    const app = appOf(src);
    const where = SAID_IN.has(app.key)
      ? `${theirs ? 'said' : 'noted'} in ${app.label}`
      : app.key === 'netti'
        ? theirs
          ? 'said to netti'
          : 'noted by netti'
        : /connected tool/i.test(src)
          ? 'captured by a connected tool'
          : `from ${src}`;
    parts.push(said ? `${where} on ${said}` : where);
  } else if (said) {
    parts.push(`${theirs ? 'said' : 'noted'} on ${said}`);
  }

  const context = String(f.context ?? '').replace(/\s+/g, ' ').trim();
  if (context) parts.push(`while working on ${context.length > 40 ? `${context.slice(0, 39).trimEnd()}…` : context}`);

  if (f.writtenBy === 'you' && (f.versions?.length ?? 0) > 0) {
    const when = shortDay(f.createdAt, nowMs);
    parts.push(when ? `you rewrote it ${when}` : 'you rewrote it');
  } else if (f.writtenBy === 'you') {
    parts.push('you wrote it');
  }
  return parts;
}

// ── the mirror's wording, by voice ──────────────────────────────────────────

/**
 * The left side of a mirror pair. "you said" is for words the person said
 * themselves (voice `said`); an AI's note that reports them is "noted about you",
 * and netti's own reading says so. A voice no writer stamped is not "said".
 */
export function mirrorSide(voice: FactVoice | null | undefined): string {
  if (voice === 'said') return 'you said';
  if (voice === 'inferred') return 'netti’s reading';
  return 'noted about you';
}

/** The mirror section's title: "what you said" only while every pair is the person's own words. */
export function mirrorTitle(pairs: ReadonlyArray<{ voice?: FactVoice | null }>): string {
  const allSaid = pairs.length === 0 || pairs.every((p) => p.voice === 'said');
  return allSaid ? 'what you said · what the record shows' : 'what’s noted about you · what the record shows';
}

// ── patterns ─────────────────────────────────────────────────────────────────

const PATTERN_KINDS: readonly NoticeKind[] = ['pattern', 'tension', 'care', 'thinking', 'question'];

export function normPatternKind(v: unknown): NoticeKind {
  const s = String(v ?? '').toLowerCase();
  return (PATTERN_KINDS as readonly string[]).includes(s) ? (s as NoticeKind) : 'pattern';
}

/** A pattern's kind as the page names it. */
export const PATTERN_KIND_WORDS: Record<string, string> = {
  pattern: 'a pattern',
  tension: 'a tension',
  care: 'something you care for',
  thinking: 'a way you think',
  question: 'a question you keep',
  intention: 'an intention',
  momentum: 'a momentum',
};

// ── deep links: which record, and what became of it ──────────────────────────

/** "fact-<id>" / "#pattern-<id>" → a pointer. Lenient about the id's alphabet
 *  (the /mirror's ids are not uuids); the kind must be one of the two. */
export function parseFocusAnchor(raw: string | null | undefined): RecordRef | null {
  const m = /^#?(fact|pattern)-([A-Za-z0-9_-]{4,80})$/.exec(String(raw ?? '').trim());
  return m ? { kind: m[1] as RecordRef['kind'], id: m[2] } : null;
}

export type FocusResolution =
  | { found: true; kind: 'fact'; id: string; changed: boolean }
  | { found: true; kind: 'pattern'; id: string; changed: false }
  | { found: false };

/**
 * Where a pointer lands on the page: the fact itself, its CURRENT version when
 * it names an earlier one (`changed`), the pattern, or nowhere (forgotten).
 */
export function resolveFocus(
  ref: RecordRef,
  facts: ReadonlyArray<Pick<PrivateFact, 'id' | 'versions'>>,
  patterns: ReadonlyArray<Pick<PrivatePattern, 'id'>>,
): FocusResolution {
  const id = String(ref.id).toLowerCase();
  if (ref.kind === 'pattern') {
    const p = patterns.find((x) => x.id.toLowerCase() === id);
    return p ? { found: true, kind: 'pattern', id: p.id, changed: false } : { found: false };
  }
  for (const f of facts) {
    if (f.id.toLowerCase() === id) return { found: true, kind: 'fact', id: f.id, changed: false };
  }
  for (const f of facts) {
    if ((f.versions ?? []).some((v) => v.id.toLowerCase() === id)) return { found: true, kind: 'fact', id: f.id, changed: true };
  }
  return { found: false };
}

// ── which app recorded what ──────────────────────────────────────────────────

/**
 * One app, whatever the raw string. activity_events.source is set from the
 * user agent (Claude, ChatGPT, Cursor, CLI, netti, mcp…); self_candidates.source
 * is a generic door or an import label (mcp-capture, chatgpt-backfill-…,
 * claude-cowork-memory-import, cursor-session). Both land on the same key.
 *
 * An agent's note (agents 146) has the source 'agent:<slug>': its own app, keyed
 * by the slug and labelled with the name the person gave it (`name`, the note's
 * source_app; the slug when a caller has none). It is checked before the
 * substring matches below, so an agent called "claude-helper" is never Claude.
 */
export function appOf(raw: string | null | undefined, name?: string | null): { key: string; label: string } {
  const s = String(raw ?? '').trim();
  const l = s.toLowerCase();
  if (!l) return { key: 'unknown', label: 'somewhere unnamed' };
  const agent = /^agent:\s*(\S.*)$/i.exec(s);
  if (agent) return { key: `agent:${agent[1].trim().toLowerCase()}`, label: String(name ?? '').trim() || agent[1].trim() };
  // the machine the innernet cli ran on (lib/machines): before the name checks, so a
  // "MacBook" is never read as the mac app, nor a machine someone named "claude" as Claude
  const machine = machineOfSource(s);
  if (machine) return { key: machineSource(machine), label: machine.name };
  if (l.includes('chatgpt') || l.includes('openai')) return { key: 'chatgpt', label: 'ChatGPT' };
  if (l.includes('claude')) return { key: 'claude', label: 'Claude' };
  if (l.includes('cursor')) return { key: 'cursor', label: 'Cursor' };
  if (l.includes('cline')) return { key: 'cline', label: 'Cline' };
  if (l.includes('antigravity')) return { key: 'antigravity', label: 'Antigravity' };
  if (l.includes('codex')) return { key: 'codex', label: 'Codex' };
  if (l.includes('gemini')) return { key: 'gemini', label: 'Gemini' };
  if (l.includes('discord')) return { key: 'discord', label: 'Discord' };
  if (l === 'cli' || l.startsWith('cli-') || l.includes('innernet-cli')) return { key: 'cli', label: 'the innernet cli' };
  if (l === 'innernet agent' || l === 'agent') return { key: 'agent', label: 'the innernet agent' };
  if (l.includes('netti')) return { key: 'netti', label: 'netti' };
  if (l.includes('extension') || l.includes('browser')) return { key: 'extension', label: 'the browser extension' };
  if (l.includes('desktop') || l.includes('mac')) return { key: 'desktop', label: 'the mac app' };
  if (l === 'mcp' || l.startsWith('mcp-') || l === 'mcp-capture') return { key: 'mcp', label: 'a connected tool, unnamed' };
  if (l === 'api' || l.startsWith('api-') || l.startsWith('v1')) return { key: 'api', label: 'the api' };
  if (l === 'web' || l === 'dashboard') return { key: 'web', label: 'innernet on the web' };
  return { key: l, label: s };
}

/** Where one fact was captured, in words: "a ChatGPT import", "Claude", … */
export function sourceLabel(raw: string | null | undefined): string | null {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  const app = appOf(s);
  if (/import|backfill/i.test(s)) return app.key === s.toLowerCase() ? 'an import' : `a ${app.label} import`;
  if (/^mcp-capture$/i.test(s)) return 'a capture from a connected tool';
  return app.label;
}

// ── quote kinds: honest labels for where a fact's words came from ───────────
//
// A fact's "words" are never claimed as the person's own unless they really
// are. `self_fact_sources.quote_kind` (migration 127, when it exists) says so
// directly; without it, the same call is made from the fact's own legacy
// columns (`verbatim`/`evidence_quote`, `evidence_start`) joined to the
// evidence row's `source`. Both paths land on the same three words.

/** Sources whose evidence is the person's OWN words: an import, a paste, a
 *  chat export they brought in themselves. */
// Only text the person handed over whole: a paste, an import, a chat export or
// a backfill of one. A client's own label (`cursor-session`, `claude-ai-chat`)
// sits on a short note that client wrote, so it reads as that client's note.
// keep identical to lib/self/factSources SAID_SOURCE (only the legacy backfill is wholly the person's)
const SAID_SOURCE_RE = /backfill/i;
/** Sources whose evidence is text an AI client wrote ABOUT the conversation —
 *  a note, not a transcript. */
const SUMMARY_SOURCE_RE = /^mcp(-|$)|^api$/i;

/** Named apps whose note can be credited by name ("Claude's note of the
 *  chat"); everything else reads as "an ai's note" — never a raw door name. */
const NAMED_QUOTE_APPS = new Set(['chatgpt', 'claude', 'cursor', 'cline', 'antigravity', 'codex', 'gemini']);

export function normQuoteKind(v: unknown): QuoteKind {
  return v === 'said' || v === 'summary' || v === 'inferred' ? v : 'summary';
}

/** What a source string says about whose words these are. Unmatched sources
 *  default to `summary`, the conservative read: a quote is never called the
 *  person's own unless the source says so plainly. */
export function quoteKindFromSource(source: string | null | undefined): QuoteKind {
  const s = String(source ?? '').trim().toLowerCase();
  if (!s) return 'summary';
  // an agent's note is never the person's words, whatever its slug says
  // ("agent:backfill-x" must not pass the backfill test below): agents (146)
  if (s.startsWith('agent:')) return 'summary';
  if (SAID_SOURCE_RE.test(s)) return 'said';
  if (SUMMARY_SOURCE_RE.test(s)) return 'summary';
  return 'summary';
}

/** The legacy call, when `self_fact_sources` holds nothing for a fact yet: no
 *  quote, or no quotable span, is `inferred` (netti's reading, not a quote);
 *  otherwise the evidence's source decides `said` vs `summary`. */
export function legacyQuoteKind(
  quote: string | null | undefined,
  evidenceStart: number | null | undefined,
  evidenceSource: string | null | undefined,
): QuoteKind {
  if (!quote || evidenceStart == null) return 'inferred';
  return quoteKindFromSource(evidenceSource);
}

/** The words beside a quote: what kind of words these are, and (for an AI's
 *  note) whose. Never says "verbatim" or "exact" for anything but `said`. */
export function quoteKindLabel(kind: QuoteKind, source: string | null | undefined): string {
  if (kind === 'said') return 'your own words';
  if (kind === 'inferred') return 'netti’s reading';
  const app = source ? appOf(source) : null;
  if (app && NAMED_QUOTE_APPS.has(app.key)) return `${app.label}’s note of the chat`;
  if (app?.key === 'netti') return 'netti’s note of the chat';
  return 'an ai’s note of the chat';
}

/** One row of `self_fact_sources`, as loosely typed as PostgREST hands it back. */
export interface FactSourceRow {
  id: unknown;
  fact_id: unknown;
  quote: unknown;
  quote_kind: unknown;
  source: unknown;
  client: unknown;
  observed_at: unknown;
  created_at?: unknown;
}

/** Group `self_fact_sources` rows by fact, each list newest-observed first.
 *  Pure — the caller reads the rows (and opens `quote_sealed`, if it ever
 *  needs to); this only shapes what is already plaintext. */
export function groupFactSources(rows: FactSourceRow[]): Map<string, FactSource[]> {
  const by = new Map<string, FactSource[]>();
  for (const r of rows) {
    const fid = r.fact_id == null ? '' : String(r.fact_id);
    if (!fid) continue;
    const list = by.get(fid) ?? [];
    list.push({
      id: r.id == null ? '' : String(r.id),
      quote: r.quote == null || r.quote === '' ? null : String(r.quote),
      quoteKind: normQuoteKind(r.quote_kind),
      source: r.source == null || r.source === '' ? null : String(r.source),
      client: r.client == null || r.client === '' ? null : String(r.client),
      observedAt: r.observed_at ? String(r.observed_at) : r.created_at ? String(r.created_at) : null,
    });
    by.set(fid, list);
  }
  for (const list of by.values()) {
    list.sort((a, b) => String(b.observedAt ?? '').localeCompare(String(a.observedAt ?? '')));
  }
  return by;
}

/** The ONE source implied by a fact's own legacy columns, when
 *  `self_fact_sources` holds nothing for it (the table doesn't exist yet, or
 *  this particular fact predates the backfill). `null` when there is truly
 *  nothing to show — no quote, no known source, no known time. */
export function legacyFactSource(input: {
  quote: string | null;
  evidenceStart: number | null;
  source: string | null;
  observedAt: string | null;
}): FactSource | null {
  if (!input.quote && !input.source && !input.observedAt) return null;
  return {
    id: 'legacy',
    quote: input.quote,
    quoteKind: legacyQuoteKind(input.quote, input.evidenceStart, input.source),
    source: input.source,
    client: null,
    observedAt: input.observedAt,
  };
}

export interface AppActivityRow {
  source: string | null;
  created_at: string;
}
export interface AppCandidateRow {
  id: string;
  source: string | null;
  source_app?: string | null;
  created_at: string;
}
/** A source counted in SQL: how many rows, the oldest, the newest. The raw is
 *  the source as stored (for captures, `source_app` over `source`). */
export interface AppTally {
  raw: string | null;
  count: number;
  firstAt: string | null;
  lastAt: string | null;
}

/** Rows as tallies of one, so rows and counted sources share one path. */
export function tallyRows(rows: Array<{ raw: string | null | undefined; at: string | null | undefined }>): AppTally[] {
  return rows.map((r) => ({ raw: r.raw ?? null, count: 1, firstAt: r.at || null, lastAt: r.at || null }));
}

/**
 * Per app: acts, captures, facts it led to, first seen → last seen. Takes rows
 * (`activity`, `candidates`) or counts made in SQL (`activityTally`,
 * `captureTally`); `candidateApps` maps a capture id to its app for the facts
 * whose capture is not among `candidates`.
 */
export function buildApps(input: {
  activity?: AppActivityRow[];
  activityTally?: AppTally[];
  candidates?: AppCandidateRow[];
  captureTally?: AppTally[];
  candidateApps?: Map<string, string | null>;
  /** sources[0] of every current fact (a self_candidates id), or null */
  factSources: Array<string | null>;
}): PrivatePayload['apps'] {
  type Acc = { key: string; label: string; acts: number; captures: number; facts: number; firstAt: string | null; lastAt: string | null };
  const by = new Map<string, Acc>();
  const touch = (raw: string | null | undefined, first: string | null | undefined, last: string | null | undefined = first) => {
    const app = appOf(raw);
    let a = by.get(app.key);
    if (!a) {
      a = { key: app.key, label: app.label, acts: 0, captures: 0, facts: 0, firstAt: null, lastAt: null };
      by.set(app.key, a);
    }
    if (first && (!a.firstAt || first < a.firstAt)) a.firstAt = first;
    if (last && (!a.lastAt || last > a.lastAt)) a.lastAt = last;
    return a;
  };

  const acts = input.activityTally ?? tallyRows((input.activity ?? []).map((r) => ({ raw: r.source, at: r.created_at })));
  for (const t of acts) touch(t.raw, t.firstAt, t.lastAt).acts += t.count;

  const candApp = new Map<string, string | null>(input.candidateApps ?? []);
  const captureRows: AppTally[] = [];
  for (const c of input.candidates ?? []) {
    // the app the capture names beats the door it came through
    const raw = c.source_app || c.source;
    candApp.set(String(c.id), raw ?? null);
    captureRows.push({ raw: raw ?? null, count: 1, firstAt: c.created_at || null, lastAt: c.created_at || null });
  }
  for (const t of input.captureTally ?? captureRows) touch(t.raw, t.firstAt, t.lastAt).captures += t.count;

  for (const id of input.factSources) {
    if (!id || !candApp.has(id)) continue;
    touch(candApp.get(id), null).facts += 1;
  }

  return [...by.values()]
    .filter((a) => a.acts + a.captures + a.facts > 0)
    .sort((a, b) => b.acts + b.captures - (a.acts + a.captures) || a.label.localeCompare(b.label))
    .map((a) => ({
      source: a.key,
      label: a.label,
      acts: a.acts,
      firstAt: a.firstAt,
      lastAt: a.lastAt,
      captures: a.captures,
      facts: a.facts,
    }));
}

/** A short look at raw text: one line, whitespace collapsed, cut on a word. */
export function preview(text: string | null | undefined, max = 160): string {
  const s = String(text ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s.,;:]+$/, '')}…`;
}
